// src/propertyManagement/pages/WorkOrdersPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box, Button, TextField, FormControl, InputLabel,
  Select, MenuItem, Checkbox, ListItemText, Typography,
  IconButton
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../../firebase';

export default function WorkOrdersPage() {
  const navigate = useNavigate();

  const [workOrders, setWorkOrders] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState(['New', 'In progress', 'Completed', 'Deferred', 'Closed']);
  const [propertyFilter, setPropertyFilter] = useState('All properties');
  const [filterOption, setFilterOption] = useState('');

  // Fetch Firestore work orders
useEffect(() => {
  const q = query(collection(db, 'workOrders'));
  const unsub = onSnapshot(q, snapshot => {
    const data = snapshot.docs.map(d => {
      const raw = d.data();
      // Turn the Firestore Timestamp into a nice string right here:
      const createdAtString = raw.createdAt && typeof raw.createdAt.toDate === 'function'
        ? raw.createdAt.toDate().toLocaleDateString()
        : '—';
      return {
        id: d.id,
        ...raw,
        createdAt: createdAtString
      };
    });
    setWorkOrders(data);
  });
  return () => unsub();
}, []);

  // Unique property options
  const allProperties = useMemo(() => {
    const unique = Array.from(new Set(workOrders.map(w => w.property || 'Unknown')));
    return ['All properties', ...unique];
  }, [workOrders]);

  const columns = [
    {
      field: 'subject',
      headerName: 'Work Order',
      flex: 2,
      renderCell: params => (
        <Link       to={`/pm/maintenance/work-orders/${params.row.id}`}
 style={{ color:'#007bff', textDecoration:'none' }}>
          {params.value}
        </Link>
      )
    },
   {
  field: 'property',
  headerName: 'Property',
  flex: 1.5,
  renderCell: params => (
    <Link
      to={`/pm/maintenance/work-orders/${params.row.id}`}
      style={{ color: '#007bff', textDecoration: 'none' }}
    >
      {params.value || '—'}
    </Link>
  )
},
  {
    field: 'createdAt',
    headerName: 'Created',
    flex: 1.5,
    renderCell: params => params.value || '—'
  },
  // … remaining columns …
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: params => (
        <span style={{
          padding:'2px 6px',
          borderRadius:4,
          background:'#e2e3e5',
          fontSize:'0.85rem'
        }}>{params.value}</span>
      )
    },
    {
      field: 'priority',
      headerName: 'Priority',
      flex: 1,
      renderCell: params => (
        <span style={{
          padding:'2px 8px',
          borderRadius:12,
          fontSize: '0.75rem',
          color: 'white',
          backgroundColor: params.value === 'High' ? '#d32f2f'
                          : params.value === 'Medium' ? '#ed6c02'
                          : '#2e7d32'
        }}>
          {params.value}
        </span>
      )
    },
    {
      field: 'imageUrl',
      headerName: 'Image',
      flex: 1,
      renderCell: params =>
        params.value ? (
          <img src={params.value} alt="thumb" style={{ height: 40, borderRadius: 4 }} />
        ) : '—'
    },
    {
      field: 'actions',
      headerName: '',
      width: 48,
      sortable: false,
      renderCell: () => (
        <IconButton size="small">
          <MoreVertIcon fontSize="small" />
        </IconButton>
      )
    }
  ];

  const filteredRows = useMemo(() => {
    return workOrders
      .filter(w =>
        selectedStatuses.includes(w.status || '') &&
        (propertyFilter === 'All properties' || w.property === propertyFilter) &&
        Object.values(w).join(' ').toLowerCase().includes(searchText.toLowerCase())
      );
  }, [workOrders, selectedStatuses, propertyFilter, searchText]);

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3, height: '100%' }}>
      <Typography variant="h5">Work orders</Typography>

      {/* Add / Export / Search */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="success"
            onClick={() => navigate('/pm/maintenance/work-orders/add')}
          >
            Add work order
          </Button>
        </Box>
        <TextField
          placeholder="Search work orders"
          variant="outlined"
          size="small"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
        {/* Property Filter */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Property</InputLabel>
          <Select
            value={propertyFilter}
            label="Property"
            onChange={e => setPropertyFilter(e.target.value)}
          >
            {allProperties.map(p => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Status Multi-select */}
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Status</InputLabel>
          <Select
            multiple
            value={selectedStatuses}
            label="Status"
            onChange={e => setSelectedStatuses(e.target.value)}
            renderValue={sel => `(${sel.length}) ${sel.join(', ')}`}
          >
            {['New', 'In progress', 'Completed', 'Deferred', 'Closed'].map(s => (
              <MenuItem key={s} value={s}>
                <Checkbox checked={selectedStatuses.includes(s)} />
                <ListItemText primary={s} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Extra filter slot (e.g. priority or assignedTo) */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Add filter option</InputLabel>
          <Select
            value={filterOption}
            label="Add filter option"
            onChange={e => setFilterOption(e.target.value)}
          >
            <MenuItem value="">Add filter option</MenuItem>
            <MenuItem value="priority">Priority</MenuItem>
            <MenuItem value="assignedTo">Assigned to</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Data Grid */}
      <Box sx={{ flexGrow: 1 }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          autoHeight={false}
          components={{ Toolbar:GridToolbar}}
        />
      </Box>
    </Box>
  );
}